import {
  createContext,
  type FC,
  type PropsWithChildren,
  type RefObject,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useReducedMotion } from "motion/react";

import { Box, type BoxProps, Row } from "@hightouch/htui";

import { navMotion } from "src/components/layout/nav/nav-motion";
import { CRUMB_BAR_MIN_HEIGHT } from "src/components/layout/page-card-metrics";
import { usePageScroll } from "src/components/layout/page-scroll";

import { useMountCount } from "src/hooks/use-mount-count";

/** The card's sticky offset as CSS, for its own sticky parts; a portaled drawer or modal outside the card gets 0. */
export const PAGE_CARD_STICKY_TOP = "var(--page-card-sticky-top, 0px)";

type PageCardValue = {
  /** False outside a page card, as for a route's loading state drawn in place of a page. */
  isInCard: boolean;
  /** The sticky crumb bar's height, which the card's own sticky parts sit below. */
  stickyTop: number;
  setStickyBarHeight: (height: number) => void;
  /** The crumb bars the page draws itself, inside the card's content, so the card draws none for it. */
  ownCrumbBarCount: number;
  registerOwnCrumbBar: () => () => void;
  /** The card scrolls and draws no crumb bar to stick, as in the settings modal, so the page's title rows stick in its place. */
  hasStickyTitleRows: boolean;
};

const PageCardContext = createContext<PageCardValue>({
  isInCard: false,
  stickyTop: 0,
  setStickyBarHeight: () => {},
  ownCrumbBarCount: 0,
  registerOwnCrumbBar: () => () => {},
  hasStickyTitleRows: false,
});

/** The page card's state, for the shell that renders the card to read and provide with `PageCardProvider`. */
export const usePageCardValue = (
  hasStickyTitleRows: boolean,
): PageCardValue => {
  // One sticky bar per card: the page's own top bar replaces the card's crumb bar.
  const [stickyTop, setStickyBarHeight] = useState(0);
  const { count: ownCrumbBarCount, register: registerOwnCrumbBar } =
    useMountCount();
  return useMemo(
    () => ({
      isInCard: true,
      stickyTop,
      setStickyBarHeight,
      ownCrumbBarCount,
      registerOwnCrumbBar,
      hasStickyTitleRows,
    }),
    [stickyTop, ownCrumbBarCount, registerOwnCrumbBar, hasStickyTitleRows],
  );
};

export const PageCardProvider = PageCardContext.Provider;

export const usePageCard = () => useContext(PageCardContext);

/** Marks a crumb bar a page draws itself inside the card's content, so the card draws none for it. */
export const OwnCrumbBarMark = () => {
  const { registerOwnCrumbBar } = usePageCard();
  // Before paint, so the card's own bar never draws a frame above the page's.
  useLayoutEffect(() => registerOwnCrumbBar(), [registerOwnCrumbBar]);
  return null;
};

/** The page's title row, and the tabs under it, sticking at the card's top where no crumb bar does. */
export const PageTitleRows: FC<PropsWithChildren> = ({ children }) => {
  const rowsRef = useRef<HTMLDivElement>(null);
  const { setStickyBarHeight, hasStickyTitleRows: isSticky } = usePageCard();

  useLayoutEffect(() => {
    const rows = rowsRef.current;
    if (!isSticky || !rows) return;
    const update = () => setStickyBarHeight(rows.offsetHeight);
    const observer = new ResizeObserver(update);
    observer.observe(rows);
    update();
    return () => {
      observer.disconnect();
      setStickyBarHeight(0);
    };
  }, [isSticky, setStickyBarHeight]);

  if (!isSticky) return children;
  return (
    <Box
      ref={rowsRef}
      position="sticky"
      top={0}
      // Above the card's own sticky parts, which sit below it.
      zIndex={3}
      flexShrink={0}
      bg="white"
      borderTopRadius="2xl"
    >
      {children}
    </Box>
  );
};

type PageCardTopBarProps = PropsWithChildren<
  BoxProps & {
    /** Sticks to the top of the window, or of the card that scrolls in its place, as the page scrolls. */
    isSticky?: boolean;
    /** Folded away to no height, as the sidebar docks; it unfolds as the sidebar collapses, in step with the fold. */
    isFolded?: boolean;
  }
>;

/** Slim bar at the top of the page card, above `PageCardHeader`. */
export const PageCardTopBar: FC<PageCardTopBarProps> = ({
  children,
  isSticky = false,
  isFolded = false,
  ...props
}) => {
  const barRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const isStuck = useIsStuck(sentinelRef, isSticky);
  const { setStickyBarHeight } = usePageCard();
  const isFolding = useFoldMotion(barRef, isFolded);

  useLayoutEffect(() => {
    const bar = barRef.current;
    if (!isSticky || !bar) return;
    const update = () => setStickyBarHeight(stuckPartsOffset(bar));
    const observer = new ResizeObserver(() => {
      // Mid-fold its height changes every frame; the card's sticky parts take the height it lands on.
      if (!isFolding.current) update();
    });
    observer.observe(bar);
    bar.addEventListener(FOLD_END_EVENT, update);
    update();
    return () => {
      observer.disconnect();
      bar.removeEventListener(FOLD_END_EVENT, update);
      setStickyBarHeight(0);
    };
  }, [isSticky, setStickyBarHeight, isFolding]);

  return (
    <>
      {/* Right above the bar, so it leaves the window as soon as content starts under the bar. */}
      {isSticky && <Box ref={sentinelRef} aria-hidden flexShrink={0} />}
      <Row
        ref={barRef}
        align="center"
        minHeight={CRUMB_BAR_MIN_HEIGHT}
        py={3}
        pl={6}
        pr={3}
        bg="white"
        // Stuck at the window's top, the card's rounded corner has scrolled away above it.
        borderTopRadius={isStuck ? undefined : "2xl"}
        borderBottomWidth="hairline"
        borderBottomStyle="solid"
        borderColor="base.divider"
        flexShrink={0}
        position={isSticky ? "sticky" : undefined}
        top={isSticky ? 0 : undefined}
        // Above the card's own sticky parts, which sit below it.
        zIndex={isSticky ? 3 : undefined}
        {...props}
        {...(isFolded && FOLDED_PROPS)}
      >
        {children}
      </Row>
    </>
  );
};

/** Where the card's sticky parts stick: tucked under the bar's hairline rule, which draws over them, so no sub-pixel sliver opens. */
const stuckPartsOffset = (bar: HTMLElement) => {
  // The used height, unrounded and untouched by a transform on the page, as offsetHeight and getBoundingClientRect are not.
  const style = getComputedStyle(bar);
  const height = Number.parseFloat(style.height) || 0;
  const rule = Number.parseFloat(style.borderBottomWidth) || 0;
  return Math.max(0, height - rule);
};

/** A folded bar: no height, padding or rule, and out of reach of the keyboard and screen readers. */
const FOLDED_PROPS = {
  height: 0,
  minHeight: 0,
  py: 0,
  borderBottomWidth: 0,
  opacity: 0,
  overflow: "hidden",
  inert: "",
  "aria-hidden": true,
} as const;

const FOLD_END_EVENT = "pagecardbarfoldend";

/** The parts of a bar's box a fold moves between its folded and its open state. */
const FOLD_KEYS = [
  "height",
  "paddingTop",
  "paddingBottom",
  "borderBottomWidth",
  "opacity",
] as const;

const foldFrame = (bar: HTMLElement): Keyframe => {
  const style = getComputedStyle(bar);
  return Object.fromEntries([
    ...FOLD_KEYS.map((key) =>
      key === "height" ? [key, `${bar.offsetHeight}px`] : [key, style[key]],
    ),
    ["minHeight", "0px"],
  ]);
};

/**
 * Glides a bar between folded and open with the sidebar's own fold: started in the same commit, on its duration and
 * easing, so the title below moves as one gesture with the page. Returns whether a glide is running.
 */
const useFoldMotion = (barRef: RefObject<HTMLElement>, isFolded: boolean) => {
  const isReducedMotion = useReducedMotion();
  const isFolding = useRef(false);
  // The box the bar last rested in, which a glide starts from.
  const lastFrame = useRef<Keyframe | null>(null);
  useLayoutEffect(() => {
    const bar = barRef.current;
    if (!bar) return;
    const from = lastFrame.current;
    const to = foldFrame(bar);
    lastFrame.current = to;
    // jsdom has no animations.
    if (!from || isReducedMotion || typeof bar.animate !== "function") return;
    // Folding away is a dock, which unfolds the sidebar; opening is a collapse.
    const duration = isFolded
      ? navMotion.unfoldDuration
      : navMotion.foldDuration;
    isFolding.current = true;
    bar.style.overflow = "hidden";
    const glide = bar.animate([from, to], {
      duration: duration * 1000,
      easing: `cubic-bezier(${navMotion.foldEase.join(", ")})`,
    });
    const end = () => {
      isFolding.current = false;
      bar.style.removeProperty("overflow");
      lastFrame.current = foldFrame(bar);
      // The resize observer skipped the glide's frames; the bar's new height reaches the card's sticky parts here.
      bar.dispatchEvent(new Event(FOLD_END_EVENT));
    };
    glide.addEventListener("finish", end);
    glide.addEventListener("cancel", end);
    return () => glide.cancel();
  }, [barRef, isFolded, isReducedMotion]);
  return isFolding;
};

/** Whether a sticky bar has content under it: the sentinel right above it has left the top of what scrolls the page. */
const useIsStuck = (
  sentinelRef: RefObject<HTMLElement>,
  isEnabled: boolean,
) => {
  const [isStuck, setIsStuck] = useState(false);
  const { element: root } = usePageScroll();
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!isEnabled || !sentinel) {
      setIsStuck(false);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        setIsStuck(
          !entry.isIntersecting &&
            entry.boundingClientRect.top < (entry.rootBounds?.top ?? 0),
        );
      },
      { root },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [sentinelRef, isEnabled, root]);
  return isStuck;
};
